// S31/AL PO 管理 / 配給(/aid/pos)— 以 ca-manager 為主。建 PO 即從憑證池配滿
// (allocate-at-create);列表 / 明細(配額使用)/ 取消(退回未配對)。reason/case_id
// 必填(治理),寫入 ca-manager audit。後端未落地 → 誠實缺口,不偽造。
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../lib/api';
import { useAuth } from '../../auth/AuthContext';
import { ErrorState, LoadingState } from '../../components/StateBlock';
import OrgCombobox from './OrgCombobox';
import { canAidAllocWrite } from './aidAllocationModel';
import {
  listPOs, getPO, createPO, cancelPO, poStatusLabel, poStatusTone, type PO, type CreatePOItem,
} from './poApi';
import '../_shared/shared.css';
import './aid.css';

const STATUS_OPTIONS = [
  { value: '', label: '全部狀態' },
  { value: 'open', label: '開立' },
  { value: 'confirmed', label: '已確認' },
  { value: 'cancelled', label: '已取消' },
];

function isPendingBackend(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 404 || err.status === 501);
}

function pct(n: number, d: number): number {
  return d > 0 ? Math.min(100, Math.round((n / d) * 100)) : 0;
}

export default function PoManagementPage() {
  const { role } = useAuth();
  const writable = canAidAllocWrite(role);

  const [fOrg, setFOrg] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [items, setItems] = useState<PO[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [selected, setSelected] = useState<PO | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(
    async (cursor?: string) => {
      setLoading(true);
      setErr(null);
      try {
        const page = await listPOs({ org_id: fOrg.trim() || undefined, status: fStatus || undefined, cursor, limit: 50 });
        setItems((prev) => (cursor ? [...prev, ...page.items] : page.items));
        setNextCursor(page.nextCursor);
      } catch (e) {
        setErr(e);
        if (!cursor) setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [fOrg, fStatus],
  );

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = useCallback(async (po: PO) => {
    setCreating(false);
    setSelected(po);
    try {
      setSelected(await getPO(po.po_number));
    } catch {
      /* keep list row values */
    }
  }, []);

  // --- create form ---
  const [creating, setCreating] = useState(false);
  const [poNumber, setPoNumber] = useState('');
  const [org, setOrg] = useState<{ id: string; name: string; aidGrant: boolean } | null>(null);
  const [rows, setRows] = useState<CreatePOItem[]>([{ type: '', amount: 0 }]);
  const [reason, setReason] = useState('');
  const [caseId, setCaseId] = useState('');
  const [busy, setBusy] = useState(false);

  const resetCreate = () => {
    setPoNumber('');
    setOrg(null);
    setRows([{ type: '', amount: 0 }]);
    setReason('');
    setCaseId('');
  };

  const submitCreate = useCallback(async () => {
    setMsg(null);
    // 忽略完全空白的明細列(type 空且數量<=0),避免多按「+新增型別」留下的空列擋住送出。
    const cleanRows = rows.filter((r) => r.type.trim() !== '' || r.amount > 0);
    const missing: string[] = [];
    if (!poNumber.trim()) missing.push('PO 號');
    if (!org) missing.push('客戶 org');
    if (!reason.trim()) missing.push('理由 reason');
    if (!caseId.trim()) missing.push('案件編號 case_id');
    if (cleanRows.length === 0) missing.push('至少一列明細(type + 數量)');
    const badRow = cleanRows.find((r) => r.type.trim() === '' || r.amount <= 0);
    if (missing.length > 0 || badRow) {
      setMsg({
        ok: false,
        text: badRow
          ? `明細未填齊 → type=「${badRow.type || '(空)'}」數量=${badRow.amount}(每列 type 必填、數量須 > 0)。`
          : `請填:${missing.join('、')}。`,
      });
      return;
    }
    if (!org) return; // narrow(上方 missing 已涵蓋)
    if (!org.aidGrant) {
      setMsg({ ok: false, text: `「${org.name}」尚無有效的 AID 生產授權(active + production_aid),無法配給;請先開通 Production Grant。` });
      return;
    }
    setBusy(true);
    try {
      await createPO({
        po_number: poNumber.trim(),
        org_id: org.id,
        org_name: org.name,
        items: cleanRows.map((r) => ({ type: r.type.trim().toUpperCase(), amount: r.amount })),
        reason: reason.trim(),
        case_id: caseId.trim(),
      });
      setMsg({ ok: true, text: `PO ${poNumber.trim()} 已建立並配給。` });
      setCreating(false);
      resetCreate();
      void load();
    } catch (e) {
      setMsg({ ok: false, text: e instanceof ApiError ? `${e.code}: ${e.message}` : String(e) });
    } finally {
      setBusy(false);
    }
  }, [poNumber, org, rows, reason, caseId, load]);

  // --- cancel ---
  const [cancelReason, setCancelReason] = useState('');
  const [cancelCase, setCancelCase] = useState('');

  const submitCancel = useCallback(async () => {
    if (!selected || !cancelReason.trim() || !cancelCase.trim()) {
      setMsg({ ok: false, text: '取消需填理由與案件編號。' });
      return;
    }
    setBusy(true);
    try {
      const res = await cancelPO(selected.po_number, cancelReason.trim(), cancelCase.trim());
      setMsg({ ok: true, text: `PO ${selected.po_number} 已取消,退回 ${res.returned} 張憑證。` });
      setCancelReason('');
      setCancelCase('');
      void openDetail(selected);
      void load();
    } catch (e) {
      setMsg({ ok: false, text: e instanceof ApiError ? `${e.code}: ${e.message}` : String(e) });
    } finally {
      setBusy(false);
    }
  }, [selected, cancelReason, cancelCase, openDetail, load]);

  return (
    <div className="aid-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">PO 配給</h1>
          <p className="page-subtitle">
            建立客戶 PO 即從憑證池配滿額度(建立即配滿)。後端 = ca-manager(<span className="adm-mono">/v3/admin/pos*</span>)。
          </p>
        </div>
        {writable && (
          <div className="adm-actionbar-btns">
            <button className="btn-primary btn-sm" onClick={() => { setCreating(true); setSelected(null); setMsg(null); }} disabled={busy}>
              建立 PO
            </button>
          </div>
        )}
      </div>

      {msg && !creating && (
        <p className={`adm-action-result ${msg.ok ? 'is-ok' : 'is-err'}`} role={msg.ok ? 'status' : 'alert'}>
          {msg.text}
        </p>
      )}

      <div className="aid-split">
        <section className="card">
          <div className="section-head">
            <div>
              <h2 className="section-title">PO 清單</h2>
              <span className="section-meta">依 org / 狀態篩選;用量 = 已配對/總量</span>
            </div>
            {loading && <span className="pill pill-neutral">查詢中</span>}
          </div>

          <div className="aid-filter-row">
            <label className="adm-field">
              <span className="adm-field-label text-secondary">org_id</span>
              <input value={fOrg} onChange={(e) => setFOrg(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void load()} placeholder="org-tw-…" />
            </label>
            <label className="adm-field">
              <span className="adm-field-label text-secondary">狀態</span>
              <select value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <button className="btn-primary btn-sm" disabled={loading} onClick={() => void load()}>套用篩選</button>
          </div>

          {err && isPendingBackend(err) ? (
            <p className="aid-gap-note">
              <strong>誠實缺口:</strong>後端 <span className="adm-mono">/v3/admin/pos*</span>(→ ca-manager)尚未落地。端點上線後即顯示真實 PO,不偽造。
            </p>
          ) : err ? (
            <ErrorState error={err} onRetry={() => void load()} />
          ) : loading && items.length === 0 ? (
            <LoadingState />
          ) : items.length > 0 ? (
            <>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>PO</th>
                      <th>客戶</th>
                      <th>用量(配對/總量)</th>
                      <th>狀態</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((p) => (
                      <tr key={p.po_number} className={`clickable ${selected?.po_number === p.po_number ? 'is-selected' : ''}`} onClick={() => void openDetail(p)}>
                        <td className="adm-mono">{p.po_number}</td>
                        <td>{p.org_name_snapshot}<div className="text-tertiary adm-mono">{p.org_id}</div></td>
                        <td>
                          <div className="aid-counts">
                            <span className="adm-mono">{p.totals.used}/{p.totals.amount}</span>
                            <div className="aid-bar" title={`已配對 ${p.totals.used} / 總量 ${p.totals.amount}`}>
                              <div className="aid-bar-consumed" style={{ width: `${pct(p.totals.used, p.totals.amount)}%` }} />
                            </div>
                          </div>
                        </td>
                        <td><span className={`pill ${poStatusTone(p.status)}`}>{poStatusLabel(p.status)}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {nextCursor && <button className="btn-ghost btn-sm" disabled={loading} onClick={() => void load(nextCursor)}>載入更多</button>}
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">📦</div>
              <div className="empty-state-title">沒有符合的 PO</div>
              <div className="empty-state-detail">調整篩選{writable ? ',或按右上「建立 PO」新增。' : '。'}</div>
            </div>
          )}
        </section>

        <aside className="aid-side">
          {creating ? (
            <section className="card card--primary">
              <div className="section-head">
                <h2 className="section-title">建立 PO(即配給)</h2>
                <button className="btn-ghost btn-sm" onClick={() => setCreating(false)} disabled={busy}>取消</button>
              </div>
              <div className="aid-form">
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">PO 號(必填)</span>
                  <input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} disabled={busy} placeholder="例:PO-2026-0001" />
                </label>
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">客戶 org(必填)</span>
                  <OrgCombobox value={org} onChange={setOrg} disabled={busy} />
                </label>
                {org && !org.aidGrant && (
                  <p className="aid-grant-warn" role="alert">
                    ⚠ 「{org.name}」尚無有效的 AID 生產授權(active + <span className="adm-mono">production_aid</span>)。
                    此 org 無法配給 AID —— 請先到「開發者組織 → {org.name} → Production Grant 治理」申請並啟用,再回來建 PO。
                  </p>
                )}
                <div className="adm-field">
                  <span className="adm-field-label text-secondary">明細(type × 數量)</span>
                  {rows.map((r, i) => (
                    <div key={i} className="aid-filter-row">
                      <input value={r.type} maxLength={3} placeholder="例:CAM" onChange={(e) => setRows((s) => s.map((x, j) => (j === i ? { ...x, type: e.target.value.toUpperCase() } : x)))} disabled={busy} />
                      <input type="number" min={1} value={r.amount || ''} placeholder="數量" onChange={(e) => setRows((s) => s.map((x, j) => (j === i ? { ...x, amount: Number(e.target.value) } : x)))} disabled={busy} />
                      {rows.length > 1 && <button className="btn-ghost btn-sm" onClick={() => setRows((s) => s.filter((_, j) => j !== i))} disabled={busy}>移除</button>}
                    </div>
                  ))}
                  <button className="btn-ghost btn-sm" onClick={() => setRows((s) => [...s, { type: '', amount: 0 }])} disabled={busy}>+ 新增 AID 類型</button>
                </div>
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">理由 reason(必填)</span>
                  <input value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy} placeholder="例:客戶首批採購配給" />
                </label>
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">案件編號 case_id(必填)</span>
                  <input value={caseId} onChange={(e) => setCaseId(e.target.value)} disabled={busy} placeholder="例:SO-2026-0001" />
                </label>
                {msg && (
                  <p className={`adm-action-result ${msg.ok ? 'is-ok' : 'is-err'}`} role={msg.ok ? 'status' : 'alert'}>
                    {msg.text}
                  </p>
                )}
                <button className="btn-primary btn-sm" onClick={() => void submitCreate()} disabled={busy}>建立並配給</button>
              </div>
            </section>
          ) : selected ? (
            <section className="card">
              <div className="section-head">
                <div>
                  <h2 className="section-title">PO 明細</h2>
                  <span className="section-meta adm-mono">{selected.po_number}</span>
                </div>
                <button className="btn-ghost btn-sm" onClick={() => setSelected(null)}>關閉</button>
              </div>
              <dl className="aid-kv-grid">
                <div className="aid-kv"><dt className="aid-kv-label">客戶</dt><dd className="aid-kv-value">{selected.org_name_snapshot} <span className="text-tertiary adm-mono">{selected.org_id}</span></dd></div>
                <div className="aid-kv"><dt className="aid-kv-label">狀態</dt><dd className="aid-kv-value"><span className={`pill ${poStatusTone(selected.status)}`}>{poStatusLabel(selected.status)}</span></dd></div>
              </dl>
              <div className="table-wrap">
                <table className="data-table">
                  <thead><tr><th>AID 類型</th><th>總量</th><th>已配對</th><th>剩餘</th></tr></thead>
                  <tbody>
                    {(selected.items ?? []).map((it) => (
                      <tr key={it.type}>
                        <td className="adm-mono">{it.type}</td>
                        <td className="adm-mono">{it.amount}</td>
                        <td className="adm-mono">{it.used}</td>
                        <td className="adm-mono">{it.remain}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {writable && selected.status !== 'cancelled' && (
                <div className="aid-form">
                  <span className="adm-field-label text-secondary">取消 PO(退回未配對憑證)</span>
                  <input className="adm-field" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="理由 reason" disabled={busy} />
                  <input className="adm-field" value={cancelCase} onChange={(e) => setCancelCase(e.target.value)} placeholder="案件編號 case_id" disabled={busy} />
                  <button className="btn-primary btn-sm" onClick={() => void submitCancel()} disabled={busy}>取消 PO</button>
                </div>
              )}
            </section>
          ) : (
            <section className="card">
              <p className="aid-hint">點選左側一列查看 PO 明細與配額使用{writable ? ',或按右上「建立 PO」。' : '。'}</p>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}
